import type { HTMLAttributes } from 'react';
import { forwardRef, useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';

export type SimpleShortenerProps = HTMLAttributes<HTMLDivElement> & {
  shortenedHeight: number;
  expandedHeight?: number | null;
  shortened?: boolean;
  onPossibilityChange?(possibility: boolean): void;
};

export const SimpleShortener = forwardRef<HTMLDivElement, SimpleShortenerProps>(
  (
    {
      children,
      shortenedHeight,
      expandedHeight = null,
      shortened = false,
      onPossibilityChange,
      ...forwardingProps
    },
    innerRef
  ) => {
    const [innerHeight, setInnerHeight] = useState<number | null>(null);
    const [isShorteningPossible, setIsShorteningPossible] = useState(false);

    const roundShortenedHeight = Math.round(shortenedHeight);

    let outerHeight = null;

    if (innerHeight !== null) {
      if (isShorteningPossible && shortened) {
        outerHeight = roundShortenedHeight;
      } else {
        outerHeight = expandedHeight !== null ? expandedHeight : innerHeight;
      }
    } else if (shortened) {
      outerHeight = roundShortenedHeight;
    }

    useEffect(() => {
      if (onPossibilityChange) {
        onPossibilityChange(isShorteningPossible);
      }
    }, [isShorteningPossible]);

    useLayoutEffect(() => {
      if (innerRef instanceof Function) {
        return;
      }

      const innerElement = innerRef?.current;

      if (!innerElement) {
        return;
      }

      const nextInnerHeight = innerElement.clientHeight;

      setInnerHeight(nextInnerHeight);
    });

    useLayoutEffect(() => {
      setIsShorteningPossible(
        (!expandedHeight || expandedHeight > roundShortenedHeight) &&
          !!innerHeight &&
          innerHeight > roundShortenedHeight
      );
    }, [roundShortenedHeight, innerHeight]);

    return (
      <Outer
        {...forwardingProps}
        _maxHeight={outerHeight}
        _preventTransition={innerHeight !== null}
      >
        <Inner ref={innerRef}>{children}</Inner>
      </Outer>
    );
  }
);

const Outer = styled.div<{
  _maxHeight: number | null;
  _preventTransition: boolean;
}>`
  position: relative;
  overflow: hidden;
  ${({ _maxHeight }) => (_maxHeight ? `max-height: ${_maxHeight}px` : '')};
  ${({ _preventTransition }) =>
    _preventTransition ? 'transition-duration: 0s !important' : ''};
`;

const Inner = styled.div``;
